import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import {
  BULK_PDF_EXPORT_LIMIT,
  GetInspectionsRequestDTO,
  InspectionColumnId,
  InspectionDTO,
  InspectionOrderByFieldEnumSchema,
  MODULE,
  PdfScope,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { z } from 'zod';

import { DeleteConfirmationDialog } from '@/components/common/delete-confirmation-dialog';
import { ShareDialog } from '@/components/common/share-dialog';
import DeleteIcon from '@/components/icons/delete';
import DownloadCsvIcon from '@/components/icons/download-csv';
import DownloadPdfIcon from '@/components/icons/download-pdf';
import LinkViewIcon from '@/components/icons/link-view';
import UndoIcon from '@/components/icons/undo';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { DownloadPdfMenu } from '@/components/table-page/download-pdf-menu';
import { TablePageHeader, TablePageHeaderActionButton, TablePageTableGroupDropdown } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { TablePageTitle } from '@/components/table-page/table-page-title';
import { useTopbarStore } from '@/components/topbar/topbar-store';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { env } from '@/env';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import ShareIcon from '@/legacy/components/icons/share';
import { buildFilterFields } from '@/queries/column-filter';
import { getInspectionsInfiniteQueryOptions, inspectionKeys } from '@/queries/inspections';
import { getUnitsQueryOptions } from '@/queries/units';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { useUserStore } from '@/stores/user.store';
import { exportRecordsToPdfZip } from '@/utils/bulk-pdf-export';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { activeExportColumnIds, buildPdfExportJob, RegisterExportQuery } from '@/utils/register-pdf-export';
import { useDropTableColumn } from '@/utils/table-meta';

import { InspectionsTable, NoInspectionAvailableDisplay, NoInspectionFoundFromFiltersDisplay, NoInspectionFoundFromSearchDisplay } from './table';
import { FILTER_FIELD_MAP, groupOptions, columns as inspectionsColumns } from './table-meta';
import { TopbarActions } from './topbar-actions';

const InspectionActivityPage = () => {
  const queryClient = useQueryClient();
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const toast = useToast();
  const { setModuleFilters } = useModulesStore();

  const { org } = useUserStore();
  const { canEditInspectionRecord, canExportCsv } = usePermissions();

  const { data: units, isLoading: isLoadingUnits } = useQuery(getUnitsQueryOptions());

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.Inspections, {
    filterFieldMap: FILTER_FIELD_MAP,
    groupOptions,
    columns: inspectionsColumns(units ?? []),
  });

  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [search, setSearch] = useState('');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isDownloadingPdfs, setIsDownloadingPdfs] = useState(false);

  const debouncedSearch = useDebounce(search, 500);

  useDropTableColumn({
    module: MODULE.Inspections,
    columnIds: ['contact'],
  });

  useLayoutEffect(() => {
    useTopbarStore.getState().setActions(<TopbarActions />);

    return () => {
      useTopbarStore.getState().setActions(<></>);
    };
  }, []);

  const {
    data: inspectionsData,
    fetchNextPage: fetchNextInspectionsPage,
    hasNextPage: hasNextInspectionsPage,
    isFetching: isFetchingInspections,
    isFetchingNextPage: isFetchingNextInspectionsPage,
    error: inspectionsError,
  } = useInfiniteQuery(
    getInspectionsInfiniteQueryOptions({
      filters: {
        search: debouncedSearch,
        ...buildFilterFields(filters, FILTER_FIELD_MAP, {
          transformValue: (id, raw) => (id === 'formNumber' ? (raw as string | undefined)?.replace(/[^0-9]/g, '') : raw),
        }),
      },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetInspectionsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy: sorting.key
        ? {
            field: (sorting.key === 'assetInternalId' ? 'asset' : sorting.key) as z.infer<typeof InspectionOrderByFieldEnumSchema>,
            direction: sorting.direction as 'asc' | 'desc',
          }
        : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const getSelectedInspections = useMemo(() => {
    return (incomingData: typeof inspectionsData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const assets = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => assets[parseInt(id)]);
    };
  }, [selectedRows]);

  const { mutate: deleteInspections, isPending: isDeletingInspections } = useMutation({
    mutationFn: async (ids: string[]) => await ApiService.DeleteInspections({ inspectionIds: ids }),
    onSuccess: () => {
      toast.success('Inspections deleted successfully');
      setOpenDeleteConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: inspectionKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleDeleteInspections = () => {
    if (inspectionsData?.pages) {
      const toDelete = getSelectedInspections(inspectionsData);
      deleteInspections(toDelete.map((inspection) => inspection.id));
    }
  };

  const selectedInspections = useMemo(() => getSelectedInspections(inspectionsData), [inspectionsData, getSelectedInspections]);
  // Members see Delete when every selected record is theirs; Admins always see
  // it. The hook function returns true for Admins regardless of the id passed.
  // NOTE: InspectionTable doesn't yet track a Web `userId` — only
  // `inspectedById` (a SitemateUser id) — so Member ownership never resolves
  // here today, matching the backend's current behaviour.
  const canBulkActOnSelectedInspections =
    selectedInspections.length > 0 && selectedInspections.every((inspection) => canEditInspectionRecord(inspection.inspectedById));

  const isRegisterPdfExportEnabled = useFeatureFlag('enableRegisterPdfExport');

  function buildInspectionsExportRequest(): RegisterExportQuery<'inspections'> {
    const selected = getSelectedInspections(inspectionsData);
    return {
      queryType: 'inspections',
      filters: { fields: { id: selected.map((inspection) => inspection.id) } },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetInspectionsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy: sorting.key
        ? {
            field: (sorting.key === 'assetInternalId' ? 'asset' : sorting.key) as z.infer<typeof InspectionOrderByFieldEnumSchema>,
            direction: sorting.direction as 'asc' | 'desc',
          }
        : undefined,
      columns: activeExportColumnIds(columnOrder),
    };
  }

  function handleCsvExport() {
    if (!inspectionsData?.pages) return;

    openExportTab({ kind: 'csv', request: { ...buildInspectionsExportRequest(), filename: buildCsvExportFilename('inspections') } });
  }

  function handlePdfScopeSelect(scope: PdfScope) {
    if (!inspectionsData?.pages) return;
    const records = getSelectedInspections(inspectionsData);
    if ((scope === 'records' || scope === 'register-and-records') && records.length > BULK_PDF_EXPORT_LIMIT) {
      toast.error(`You can export up to ${BULK_PDF_EXPORT_LIMIT} records at a time. Deselect some and try again.`);
      return;
    }
    openExportTab(
      buildPdfExportJob({
        scope,
        recordType: 'inspection',
        records,
        register: buildInspectionsExportRequest(),
      }),
    );
  }

  async function handlePdfExportZip() {
    if (!inspectionsData?.pages) return;
    setIsDownloadingPdfs(true);
    try {
      await exportRecordsToPdfZip(getSelectedInspections(inspectionsData), 'inspections');
      toast.success('PDF export completed');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to export PDFs');
    } finally {
      setIsDownloadingPdfs(false);
    }
  }

  useEffect(() => {
    if (inspectionsError) {
      toast.error(inspectionsError.message);
    }
  }, [toast, inspectionsError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextInspectionsPage,
    hasNextPage: hasNextInspectionsPage,
    isFetching: isFetchingInspections,
    isError: !!inspectionsError,
    root: scrollRoot,
  });

  const flattenedData = useMemo(() => inspectionsData?.pages.flatMap((page) => page.data) ?? [], [inspectionsData]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedInspections.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <TablePageTitle>Activity</TablePageTitle>
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.Inspections, [])}
                    />
                  )}
                </div>

                <div className="mr-1 flex h-full items-center">
                  <div className="flex items-center">
                    <TablePageTableGroupDropdown
                      columns={groupOptions}
                      value={grouping.length ? grouping[0] : ''}
                      onChange={(value) =>
                        updatePageConfig({
                          table: { grouping: value ? [value] : [] },
                        })
                      }
                    />
                    <ColumnsDialog<InspectionDTO>
                      open={openColumnsDialog}
                      defaultSectionLabel="Inspection"
                      onOpenChange={setOpenColumnsDialog}
                      columns={inspectionsColumns(units ?? [])}
                      columnOrder={columnOrder}
                      onColumnOrderChange={(columnOrder) =>
                        updatePageConfig({
                          table: { columnOrder: columnOrder as Array<{ id: InspectionColumnId; active: boolean }> },
                        })
                      }
                    />
                    <TablePageHeaderActionButton
                      onClick={() => setShareUrl(`${env.VITE_FORMS_URL}/${org?.id}/inspection`)}
                      icon={<ShareIcon />}
                      label="Share Link"
                    />
                    <TablePageHeaderActionButton
                      onClick={() => window.open(`${env.VITE_FORMS_URL}/${org?.id}/inspection`, '_blank')}
                      icon={<LinkViewIcon />}
                      label="Open Operator Web App"
                    />
                  </div>
                </div>
              </>
            )}

            {selectedInspections.length > 0 && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedInspections.length} totalCount={inspectionsData?.pages[0]?.totalCount ?? 0} />
                {canExportCsv && <TablePageHeaderActionButton icon={<DownloadCsvIcon />} label="Download CSV" onClick={handleCsvExport} />}
                {isRegisterPdfExportEnabled ? (
                  <DownloadPdfMenu recordNoun="inspection" onSelect={handlePdfScopeSelect} canExportRegister={canExportCsv} />
                ) : (
                  <TablePageHeaderActionButton icon={<DownloadPdfIcon />} label="Download PDF" onClick={handlePdfExportZip} disabled={isDownloadingPdfs} />
                )}
                {canBulkActOnSelectedInspections && (
                  <TablePageHeaderActionButton
                    icon={<DeleteIcon className="text-default-red" />}
                    label="Delete"
                    onClick={() => setOpenDeleteConfirmation(true)}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <InspectionsTable
          data={flattenedData}
          loading={(isFetchingInspections && !isFetchingNextInspectionsPage) || isLoadingPageConfig || isLoadingUnits}
          units={units ?? []}
          notFoundDisplay={
            debouncedSearch.length > 0 ? (
              <NoInspectionFoundFromSearchDisplay />
            ) : filters && filters.length > 0 ? (
              <NoInspectionFoundFromFiltersDisplay removeColumnFilters={() => setModuleFilters(MODULE.Inspections, [])} />
            ) : (
              <NoInspectionAvailableDisplay />
            )
          }
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters) => setModuleFilters(MODULE.Inspections, filters)}
          sorting={sorting}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          onSortingChange={(newSorting) =>
            updatePageConfig({
              table: { sorting: newSorting },
            })
          }
        />
        {hasNextInspectionsPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextInspectionsPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>

      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteInspections}
        loading={isDeletingInspections}
        title="Delete inspections?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete these inspections?"
      />

      {shareUrl && <ShareDialog url={shareUrl} onOpenChange={() => setShareUrl(null)} open={!!shareUrl} />}
    </div>
  );
};

export default InspectionActivityPage;
